import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from '@contrast-security-inc/design-system-typography'
import { Header, Item, Panel } from '.'
import { PanelProps } from './panel'
import { HeaderProps } from './header'
import { ItemProps } from './item'
import {
  CustomSlidingTutorialProps,
  SlidingTutorialContent
} from './sliding-tutorial-content'
import appOverviewClipboardIcon from '../../../images/tutorial/appOverviewClipboardIcon.png'
import appOverviewEnvironmentIcon from '../../../images/tutorial/appOverviewEnvironmentIcon.png'
import appOverviewReportIcon from '../../../images/tutorial/appOverviewReportIcon.png'
import appOverviewScoreIcon from '../../../images/tutorial/appOverviewScoreIcon.png'
import {
  getLibrariesRoute,
  getApplicationLibrariesRoute,
  getVulnerabilitiesRoute,
  getApplicationVulnerabilitiesRoute
} from '../route'
import { Button } from '@contrast-security-inc/design-system-buttons'

export function ApplicationSlidingTutorial(props: CustomSlidingTutorialProps) {
  const intl = useIntl()

  const librariesLink = props.appId
    ? getApplicationLibrariesRoute(props.orgUuid, props.appId)
    : getLibrariesRoute(props.orgUuid)

  const vulnerabilitiesLink = props.appId
    ? getApplicationVulnerabilitiesRoute(props.orgUuid, props.appId)
    : getVulnerabilitiesRoute(props.orgUuid)

  const itemsConfig: ItemProps[] = [
    {
      title: {
        id: 'title_appSlidingTutorial_viewDetails'
      },
      description: {
        id: 'detail_appSlidingTutorial_viewDetails'
      },
      imagePath: appOverviewClipboardIcon,
      circleIndicators: [
        {
          selector: '[tutorial-highlight="appPolicy"]',
          radius: 40,
          yOffset: -21,
          xOffset: -8,
          zIndex: 10
        },
        {
          selector: '[tutorial-highlight="appLoC"]',
          radius: 90,
          yOffset: -38,
          xOffset: -26,
          zIndex: 10
        }
      ]
    },
    {
      title: {
        id: 'title_appSlidingTutorial_getFindings'
      },
      description: {
        id: 'detail_appSlidingTutorial_getFindings'
      },
      imagePath: appOverviewReportIcon,
      circleIndicators: [
        {
          selector: '[tutorial-highlight="appVulnsChart"]',
          radius: 100,
          yOffset: -70,
          xOffset: -40
        }
      ]
    },
    {
      title: {
        id: 'title_appSlidingTutorial_improveScore'
      },
      description: {
        id: 'variable_appSlidingTutorial_improveScore',
        values: {
          div: (...msg) => <div>{msg}</div>,
          a3: (...msg) => (
            <Link
              href={intl.formatMessage({
                id: 'link_applicationScoringGuide'
              })}
              isExternal={true}
            >
              {msg}
            </Link>
          ),
          a2: (...msg) => (
            <Link href={librariesLink} isExternal={true}>
              {msg}
            </Link>
          ),
          a1: (...msg) => (
            <Link href={vulnerabilitiesLink} isExternal={true}>
              {msg}
            </Link>
          )
        }
      },
      imagePath: appOverviewScoreIcon,
      circleIndicators: [
        {
          selector: '[tutorial-highlight="appScore"]',
          radius: 78,
          yOffset: -36,
          xOffset: -36
        }
      ]
    },
    {
      title: {
        id: 'title_appSlidingTutorial_setUpEnvironment'
      },
      description: {
        id: 'detail_appSlidingTutorial_setUpEnvironment',
        values: {
          div: (...msg) => <div>{msg}</div>,
          a: (...msg) => (
            <Link
              href={intl.formatMessage({ id: 'link_servers' })}
              isExternal={true}
            >
              {msg}
            </Link>
          )
        }
      },
      imagePath: appOverviewEnvironmentIcon,
      circleIndicators: [
        {
          selector: '[tutorial-highlight="appEnvironment"]',
          radius: 56,
          yOffset: -38,
          xOffset: -18
        }
      ]
    }
  ]

  const headerConfig: HeaderProps = {
    title: {
      id: 'title_appSlidingTutorial_diveIn'
    },
    subtitle: {
      id: 'detail_appSlidingTutorial_getAPerspective'
    }
  }

  const panelConfig: PanelProps = {
    renderHeader: () => {
      return <Header {...headerConfig} />
    },
    renderBody: () =>
      itemsConfig.map((item, index) => <Item key={index} {...item} />),
    renderFooter: (renderFooterProps) => {
      return (
        <Button {...renderFooterProps}>
          <FormattedMessage id="action_tellMeMore" />
        </Button>
      )
    }
  }

  return props.isVisible ? (
    <SlidingTutorialContent
      isOpen={props.isOpen}
      renderPanel={(renderPanelProps) => {
        return <Panel {...renderPanelProps} {...panelConfig} />
      }}
      tutorialKey={props.tutorialKey}
      orgUuid={props.orgUuid}
      yOffset={props.yOffset}
      route={vulnerabilitiesLink}
      zIndex={props.zIndex}
    />
  ) : null
}
